import React from 'react';
import styled, { css } from 'styled-components';
import { colors, transitions, borderRadius, shadows, componentSizes } from '../../theme/tokens';

export enum CardVariant {
  DEFAULT = 'default',
  ELEVATED = 'elevated',
  GLASS = 'glass'
}

export enum CardPadding {
  NONE = 'none',
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  fullHeight?: boolean;
  hover?: boolean;
  children?: React.ReactNode;
}

const paddingStyles = {
  [CardPadding.NONE]: css`
    padding: ${componentSizes.card.padding.none};
  `,
  [CardPadding.SM]: css`
    padding: ${componentSizes.card.padding.sm};
  `,
  [CardPadding.MD]: css`
    padding: ${componentSizes.card.padding.md};
  `,
  [CardPadding.LG]: css`
    padding: ${componentSizes.card.padding.lg};
  `
};

const variantStyles = {
  [CardVariant.DEFAULT]: css`
    background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
    border: 1px solid #e0e8f5;
    box-shadow: ${shadows.card.default};
  `,
  [CardVariant.ELEVATED]: css`
    background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
    border: 1px solid #e0e8f5;
    box-shadow: ${shadows.card.elevated};
  `,
  [CardVariant.GLASS]: css`
    background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
    border: 1px solid #e0e8f5;
    box-shadow: ${shadows.card.glass};
    backdrop-filter: blur(10px);
  `
};

const hoverStyles = ({ variant = CardVariant.DEFAULT }: CardProps) => css`
  &:hover {
    transform: translateY(-5px);
    background: linear-gradient(145deg, #f5f8fd 0%, #ffffff 100%);
    box-shadow: ${variant === CardVariant.GLASS ? shadows.card.glassHover : shadows.lg};
    border-color: #cad5e9;
  }
`;

const StyledCard = styled.div<CardProps>`
  border-radius: ${borderRadius.xl};
  width: 100%;
  position: relative;
  text-align: left;
  display: flex;
  flex-direction: column;
  transition: all ${transitions.duration.long} ${transitions.timing};
  transform: none;

  ${({ variant = CardVariant.DEFAULT }) => variantStyles[variant]}
  ${({ padding = CardPadding.MD }) => paddingStyles[padding]}
  ${({ fullHeight }) => fullHeight && css`
    height: 100%;
  `}
  ${({ hover, variant }) => hover && hoverStyles({ variant })}
`;

export const Card: React.FC<CardProps> = ({
  children,
  variant = CardVariant.DEFAULT,
  padding = CardPadding.MD,
  fullHeight = false,
  hover = false,
  ...props
}) => {
  return (
    <StyledCard
      variant={variant}
      padding={padding}
      fullHeight={fullHeight}
      hover={hover}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

export default Card;